import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  AddressEditor,
} from '@wove/web-ui'
import { useEffect, useState } from 'react'
import { Loader2, Save, Plus, Trash2, Search } from 'lucide-react'
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { ShipmentDetails, ShipmentItem, ShipmentContainer } from '../types/shipmentTypes'
import { Address, LocationAddress, PortAddress, AirportAddress } from '@wove/common'
import HsCodeSearchDialog from './HsCodeSearchDialog'
import HsCodeInfoModal from './HsCodeInfoModal'
import { toast } from 'sonner'
import { HelpCircle } from 'lucide-react'
import { getHsCodeDetails } from '@wove/api'
import { UnitTypeSelect } from './UnitTypeSelect'
import { UnitTypeCategory } from '@wove/common/units'
import { ShipmentType, ShipmentTypeLabels, isOceanFreight, LocationType, LocationTypeLabels } from '@wove/common'
import AutoWidthSelect from './AutoWidthSelect'

interface ConsolidatedShipmentDetailsViewProps {
  shipmentId: string
  onSave?: (shipmentId: string, details: ShipmentDetails) => Promise<void>
  onLoad: (shipmentId: string) => Promise<ShipmentDetails>
  onSearchPorts?: (query: string) => Promise<any[]>
  onSearchAirports?: (query: string) => Promise<any[]>
  onSearchHsCodes?: (query: string) => Promise<Array<{ code: string; description: string }>>
  showSaveButton?: boolean
  showCloseButton?: boolean
  onClose?: () => void
}

export default function ConsolidatedShipmentDetailsView({
  shipmentId,
  onSave,
  onLoad,
  onSearchPorts,
  onSearchAirports,
  onSearchHsCodes,
  showSaveButton = true,
  showCloseButton = true,
  onClose,
}: ConsolidatedShipmentDetailsViewProps) {
  const [details, setDetails] = useState<ShipmentDetails | null>(null)
  const [editedDetails, setEditedDetails] = useState<ShipmentDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hsCodeDialogOpen, setHsCodeDialogOpen] = useState(false)
  const [currentItemIndex, setCurrentItemIndex] = useState<number | null>(null)
  const [hsCodeInfoOpen, setHsCodeInfoOpen] = useState(false)
  const [hsCodeInfoData, setHsCodeInfoData] = useState<{ hsCode?: string; description?: string }>({})

  useEffect(() => {
    if (!shipmentId) {
      setError('No shipment ID provided')
      setLoading(false)
      return
    }

    onLoad(shipmentId)
      .then(data => {
        // Ensure data has the expected structure
        const shipmentData = {
          shipment: data.shipment || {
            referenceNumbers: [],
          },
          items: data.items || [],
          containers: data.containers || [],
        } satisfies ShipmentDetails
        setDetails(shipmentData)
        setEditedDetails(JSON.parse(JSON.stringify(shipmentData))) // Deep copy
      })
      .catch(err => {
        console.error('Error fetching shipment details:', err)
        setError('Failed to load shipment details')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [shipmentId, onLoad])

  const handleItemChange = (index: number, field: keyof ShipmentItem, value: any) => {
    if (!editedDetails) return

    const updatedItems = [...editedDetails.items]
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    }

    setEditedDetails({
      ...editedDetails,
      items: updatedItems,
    })
  }

  const handleContainerChange = (index: number, field: keyof ShipmentContainer, value: string) => {
    if (!editedDetails) return

    const updatedContainers = [...editedDetails.containers]
    updatedContainers[index] = {
      ...updatedContainers[index],
      [field]: value,
    }

    setEditedDetails({
      ...editedDetails,
      containers: updatedContainers,
    })
  }

  const handleSave = async () => {
    if (!editedDetails || !onSave) return

    setSaving(true)
    try {
      await onSave(shipmentId, editedDetails)
      setDetails(editedDetails)
      toast.success('Changes saved', {
        description: 'Shipment details have been updated successfully.',
      })
    } catch (err) {
      console.error('Error saving shipment details:', err)
      toast.error('Error saving changes', {
        description: 'There was a problem updating the shipment details. Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  const hasChanges = () => {
    return JSON.stringify(details) !== JSON.stringify(editedDetails)
  }

  const handleAddItem = () => {
    if (!editedDetails) return

    const newItem: ShipmentItem = {
      lineNumber: editedDetails.items.length + 1,
      sku: '',
      partNumber: '',
      description: '',
      quantity: 0,
      unitOfMeasure: 'pcs',
      unitPrice: 0,
      amount: 0,
      currency: 'USD',
      countryOfOrigin: '',
      hsCode: '',
      weight: 0,
      weightUnit: 'kg',
      dimensions: {
        length: 0,
        width: 0,
        height: 0,
        unit: 'cm',
      },
      volume: 0,
      volumeUnit: 'm³',
      packageNumber: '',
    }

    setEditedDetails({
      ...editedDetails,
      items: [...editedDetails.items, newItem],
    })
  }

  const handleRemoveItem = (index: number) => {
    if (!editedDetails) return

    const updatedItems = [...editedDetails.items]
    updatedItems.splice(index, 1)

    // Update line numbers for remaining items
    updatedItems.forEach((item, idx) => {
      item.lineNumber = idx + 1
    })

    setEditedDetails({
      ...editedDetails,
      items: updatedItems,
    })
  }

  const handleAddContainer = () => {
    if (!editedDetails) return

    const newContainer: ShipmentContainer = {
      number: '',
      size: '',
      type: '',
      sealNumber: '',
    }

    setEditedDetails({
      ...editedDetails,
      containers: [...editedDetails.containers, newContainer],
    })
  }

  const handleRemoveContainer = (index: number) => {
    if (!editedDetails) return

    const updatedContainers = [...editedDetails.containers]
    updatedContainers.splice(index, 1)

    setEditedDetails({
      ...editedDetails,
      containers: updatedContainers,
    })
  }

  const handleHsCodeSelect = (hsCode: string) => {
    if (currentItemIndex !== null && editedDetails) {
      handleItemChange(currentItemIndex, 'hsCode', hsCode)
    }
  }

  const openHsCodeDialog = (index: number) => {
    setCurrentItemIndex(index)
    setHsCodeDialogOpen(true)
  }

  const showHsCodeInfo = async (hsCode: string | undefined, description: string | undefined) => {
    if (!hsCode) {
      setHsCodeInfoData({ hsCode, description })
      setHsCodeInfoOpen(true)
      return
    }

    try {
      const details = await getHsCodeDetails(hsCode)
      setHsCodeInfoData({ hsCode: details.code, description: details.description })
      setHsCodeInfoOpen(true)
    } catch (error) {
      console.error('Error fetching HS code details:', error)
      // Fall back to using the provided description if API call fails
      setHsCodeInfoData({ hsCode, description })
      setHsCodeInfoOpen(true)
    }
  }

  const getPlaceOfReceiptAddress = (type: LocationType) => {
    if (!editedDetails) return null
    switch (type) {
      case LocationType.PORT:
        return editedDetails.shipment.placeOfReceiptPort
      case LocationType.AIRPORT:
        return editedDetails.shipment.placeOfReceiptAirport
      case LocationType.DOOR:
        return editedDetails.shipment.placeOfReceiptAddress
      default:
        return null
    }
  }

  const getPlaceOfDeliveryAddress = (type: LocationType) => {
    if (!editedDetails) return null
    switch (type) {
      case LocationType.PORT:
        return editedDetails.shipment.placeOfDeliveryPort
      case LocationType.AIRPORT:
        return editedDetails.shipment.placeOfDeliveryAirport
      case LocationType.DOOR:
        return editedDetails.shipment.placeOfDeliveryAddress
      default:
        return null
    }
  }

  const setPlaceOfReceiptAddress = (type: LocationType, address: Address) => {
    if (!editedDetails) return
    switch (type) {
      case LocationType.PORT:
        setEditedDetails({
          ...editedDetails,
          shipment: { ...editedDetails.shipment, placeOfReceiptPort: address as PortAddress },
          items: editedDetails.items || [],
          containers: editedDetails.containers || [],
        })
        break
      case LocationType.AIRPORT:
        setEditedDetails({
          ...editedDetails,
          shipment: { ...editedDetails.shipment, placeOfReceiptAirport: address as AirportAddress },
          items: editedDetails.items || [],
          containers: editedDetails.containers || [],
        })
        break
      case LocationType.DOOR:
        setEditedDetails({ 
          ...editedDetails, 
          shipment: { ...editedDetails.shipment, placeOfReceiptAddress: address },
          items: editedDetails.items || [],
          containers: editedDetails.containers || [],
        })
        break
      default:
        break
    }
  }

  const setPlaceOfDeliveryAddress = (type: LocationType, address: Address) => {
    if (!editedDetails) return
    switch (type) {
      case LocationType.PORT:
        setEditedDetails({
          ...editedDetails,
          shipment: { ...editedDetails.shipment, placeOfDeliveryPort: address as PortAddress },
          items: editedDetails.items || [],
          containers: editedDetails.containers || [],
        })
        break
      case LocationType.AIRPORT:
        setEditedDetails({
          ...editedDetails,
          shipment: { ...editedDetails.shipment, placeOfDeliveryAirport: address as AirportAddress },
          items: editedDetails.items || [],
          containers: editedDetails.containers || [],
        })
        break
      case LocationType.DOOR:
        setEditedDetails({
          ...editedDetails,
          shipment: { ...editedDetails.shipment, placeOfDeliveryAddress: address as LocationAddress },
          items: editedDetails.items || [],
          containers: editedDetails.containers || [],
        })
        break
      default:
        break
    }
  }

  return (
    <div className="p-6 max-h-screen flex flex-col h-full">
      <div className="mb-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Shipment Details</h1>
        {editedDetails && showSaveButton && onSave && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setEditedDetails(JSON.parse(JSON.stringify(details)))}
              disabled={!hasChanges() || saving}
            >
              Reset
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges() || saving} className="gap-2">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 flex-1">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-500 flex-1">{error}</div>
      ) : editedDetails ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          <Tabs defaultValue="details" className="flex-1 flex flex-col">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="items">Items ({editedDetails?.items?.length || 0})</TabsTrigger>
              <TabsTrigger value="containers">Containers ({editedDetails?.containers?.length || 0})</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-auto mt-4">
              <TabsContent value="details" className="h-full">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium mb-2">Reference Numbers</h3>
                      <div className="space-y-2">
                        {editedDetails.shipment.referenceNumbers?.map((refNumber, index) => (
                          <div key={index} className="grid grid-cols-3 gap-2 items-end">
                            <div>
                              <label className="text-xs text-gray-500">Type</label>
                              <Input
                                value={refNumber.type || ''}
                                onChange={e => {
                                  const updatedRefNumbers = [...editedDetails.shipment.referenceNumbers]
                                  updatedRefNumbers[index] = {
                                    ...updatedRefNumbers[index],
                                    type: e.target.value,
                                  }
                                  setEditedDetails({
                                    ...editedDetails,
                                    shipment: {
                                      ...editedDetails.shipment,
                                      referenceNumbers: updatedRefNumbers,
                                    },
                                  })
                                }}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Number</label>
                              <Input
                                value={refNumber.displayNumber || ''}
                                onChange={e => {
                                  const updatedRefNumbers = [...editedDetails.shipment.referenceNumbers]
                                  updatedRefNumbers[index] = {
                                    ...updatedRefNumbers[index],
                                    displayNumber: e.target.value,
                                    cleanedNumber: e.target.value.replace(/\D/g, ''),
                                  }
                                  setEditedDetails({
                                    ...editedDetails,
                                    shipment: {
                                      ...editedDetails.shipment,
                                      referenceNumbers: updatedRefNumbers,
                                    },
                                  })
                                }}
                              />
                            </div>
                            <div className="flex">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updatedRefNumbers = [...editedDetails.shipment.referenceNumbers]
                                  updatedRefNumbers.splice(index, 1)
                                  setEditedDetails({
                                    ...editedDetails,
                                    shipment: {
                                      ...editedDetails.shipment,
                                      referenceNumbers: updatedRefNumbers,
                                    },
                                  })
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newRefNumber = {
                              type: '',
                              displayNumber: '',
                              cleanedNumber: '',
                            }
                            setEditedDetails({
                              ...editedDetails,
                              shipment: {
                                ...editedDetails.shipment,
                                referenceNumbers: [...(editedDetails.shipment.referenceNumbers || []), newRefNumber],
                              },
                            })
                          }}
                          className="flex items-center gap-1 w-full"
                        >
                          <Plus className="h-4 w-4" />
                          Add Reference Number
                        </Button>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium mb-2">Locations</h3>
                      <div className="space-y-2">
                        {isOceanFreight(editedDetails.shipment.shipmentType) && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-gray-500">Port of Loading</label>
                              <AddressEditor
                                value={editedDetails.shipment.portOfLoading || ({} as Address)}
                                onChange={value =>
                                  setEditedDetails({
                                    ...editedDetails,
                                    shipment: {
                                      ...editedDetails.shipment,
                                      portOfLoading: value,
                                    },
                                  })
                                }
                                type={LocationType.PORT}
                                searchPorts={onSearchPorts}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Port of Discharge</label>
                              <AddressEditor
                                value={editedDetails.shipment.portOfDischarge || ({} as Address)}
                                onChange={value =>
                                  setEditedDetails({
                                    ...editedDetails,
                                    shipment: {
                                      ...editedDetails.shipment,
                                      portOfDischarge: value,
                                    },
                                  })
                                }
                                type={LocationType.PORT}
                                searchPorts={onSearchPorts}
                              />
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <label className="text-xs text-gray-500">Place of Receipt</label>
                              <Select
                                value={editedDetails.shipment.placeOfReceiptType || LocationType.PORT}
                                onValueChange={value => {
                                  setEditedDetails({
                                    ...editedDetails,
                                    shipment: {
                                      ...editedDetails.shipment,
                                      placeOfReceiptType: value as LocationType,
                                    },
                                  })
                                }}
                              >
                                <SelectTrigger className="h-5 w-20 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(LocationTypeLabels).map(([value, label]) => (
                                    <SelectItem key={value} value={value}>
                                      {label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <AddressEditor
                              value={getPlaceOfReceiptAddress(editedDetails.shipment.placeOfReceiptType || LocationType.PORT) || ({} as Address)}
                              onChange={value =>
                                setPlaceOfReceiptAddress(editedDetails.shipment.placeOfReceiptType || LocationType.PORT, value)
                              }
                              type={editedDetails.shipment.placeOfReceiptType || LocationType.PORT}
                              searchPorts={onSearchPorts}
                              searchAirports={onSearchAirports}
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <label className="text-xs text-gray-500">Place of Delivery</label>
                              <Select
                                value={editedDetails.shipment.placeOfDeliveryType || LocationType.PORT}
                                onValueChange={value => {
                                  setEditedDetails({
                                    ...editedDetails,
                                    shipment: {
                                      ...editedDetails.shipment,
                                      placeOfDeliveryType: value as LocationType,
                                    },
                                  })
                                }}
                              >
                                <SelectTrigger className="h-5 w-20 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(LocationTypeLabels).map(([value, label]) => (
                                    <SelectItem key={value} value={value}>
                                      {label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <AddressEditor
                              value={getPlaceOfDeliveryAddress(editedDetails.shipment.placeOfDeliveryType || LocationType.PORT) || ({} as Address)}
                              onChange={value =>
                                setPlaceOfDeliveryAddress(editedDetails.shipment.placeOfDeliveryType || LocationType.PORT, value)
                              }
                              type={editedDetails.shipment.placeOfDeliveryType || LocationType.PORT}
                              searchPorts={onSearchPorts}
                              searchAirports={onSearchAirports}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium mb-2">Dates</h3>
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Invoice Date</label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start text-left font-normal">
                                  {editedDetails.shipment.invoiceDate ? (
                                    format(new Date(editedDetails.shipment.invoiceDate), 'PPP')
                                  ) : (
                                    <span className="text-muted-foreground">Select date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={
                                    editedDetails.shipment.invoiceDate
                                      ? new Date(editedDetails.shipment.invoiceDate)
                                      : undefined
                                  }
                                  onSelect={date =>
                                    setEditedDetails({
                                      ...editedDetails,
                                      shipment: {
                                        ...editedDetails.shipment,
                                        invoiceDate: date ? date.toISOString().split('T')[0] : undefined,
                                      },
                                    })
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Due Date</label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start text-left font-normal">
                                  {editedDetails.shipment.dueDate ? (
                                    format(new Date(editedDetails.shipment.dueDate), 'PPP')
                                  ) : (
                                    <span className="text-muted-foreground">Select date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={
                                    editedDetails.shipment.dueDate
                                      ? new Date(editedDetails.shipment.dueDate)
                                      : undefined
                                  }
                                  onSelect={date =>
                                    setEditedDetails({
                                      ...editedDetails,
                                      shipment: {
                                        ...editedDetails.shipment,
                                        dueDate: date ? date.toISOString().split('T')[0] : undefined,
                                      },
                                    })
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Issue Date</label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start text-left font-normal">
                                  {editedDetails.shipment.issueDate ? (
                                    format(new Date(editedDetails.shipment.issueDate), 'PPP')
                                  ) : (
                                    <span className="text-muted-foreground">Select date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={
                                    editedDetails.shipment.issueDate
                                      ? new Date(editedDetails.shipment.issueDate)
                                      : undefined
                                  }
                                  onSelect={date =>
                                    setEditedDetails({
                                      ...editedDetails,
                                      shipment: {
                                        ...editedDetails.shipment,
                                        issueDate: date ? date.toISOString().split('T')[0] : undefined,
                                      },
                                    })
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Shipment Date</label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start text-left font-normal">
                                  {editedDetails.shipment.shipmentDate ? (
                                    format(new Date(editedDetails.shipment.shipmentDate), 'PPP')
                                  ) : (
                                    <span className="text-muted-foreground">Select date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={
                                    editedDetails.shipment.shipmentDate
                                      ? new Date(editedDetails.shipment.shipmentDate)
                                      : undefined
                                  }
                                  onSelect={date =>
                                    setEditedDetails({
                                      ...editedDetails,
                                      shipment: {
                                        ...editedDetails.shipment,
                                        shipmentDate: date ? date.toISOString().split('T')[0] : undefined,
                                      },
                                    })
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Expected Delivery Date</label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start text-left font-normal">
                                  {editedDetails.shipment.expectedDeliveryDate ? (
                                    format(new Date(editedDetails.shipment.expectedDeliveryDate), 'PPP')
                                  ) : (
                                    <span className="text-muted-foreground">Select date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={
                                    editedDetails.shipment.expectedDeliveryDate
                                      ? new Date(editedDetails.shipment.expectedDeliveryDate)
                                      : undefined
                                  }
                                  onSelect={date =>
                                    setEditedDetails({
                                      ...editedDetails,
                                      shipment: {
                                        ...editedDetails.shipment,
                                        expectedDeliveryDate: date ? date.toISOString().split('T')[0] : undefined,
                                      },
                                    })
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Ready Date</label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start text-left font-normal">
                                  {editedDetails.shipment.readyDate ? (
                                    format(new Date(editedDetails.shipment.readyDate), 'PPP')
                                  ) : (
                                    <span className="text-muted-foreground">Select date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={
                                    editedDetails.shipment.readyDate
                                      ? new Date(editedDetails.shipment.readyDate)
                                      : undefined
                                  }
                                  onSelect={date =>
                                    setEditedDetails({
                                      ...editedDetails,
                                      shipment: {
                                        ...editedDetails.shipment,
                                        readyDate: date ? date.toISOString().split('T')[0] : undefined,
                                      },
                                    })
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Date of Signature</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-left font-normal">
                                {editedDetails.shipment.dateOfSignature ? (
                                  format(new Date(editedDetails.shipment.dateOfSignature), 'PPP')
                                ) : (
                                  <span className="text-muted-foreground">Select date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={
                                  editedDetails.shipment.dateOfSignature
                                    ? new Date(editedDetails.shipment.dateOfSignature)
                                    : undefined
                                }
                                onSelect={date =>
                                  setEditedDetails({
                                    ...editedDetails,
                                    shipment: {
                                      ...editedDetails.shipment,
                                      dateOfSignature: date ? date.toISOString().split('T')[0] : undefined,
                                    },
                                  })
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium mb-2">Transport Details</h3>
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Carrier</label>
                            <Input
                              value={editedDetails.shipment.carrier || ''}
                              onChange={e =>
                                setEditedDetails({
                                  ...editedDetails,
                                  shipment: {
                                    ...editedDetails.shipment,
                                    carrier: e.target.value,
                                  },
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Vessel</label>
                            <Input
                              value={editedDetails.shipment.vessel || ''}
                              onChange={e =>
                                setEditedDetails({
                                  ...editedDetails,
                                  shipment: {
                                    ...editedDetails.shipment,
                                    vessel: e.target.value,
                                  },
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Voyage</label>
                            <Input
                              value={editedDetails.shipment.voyage || ''}
                              onChange={e =>
                                setEditedDetails({
                                  ...editedDetails,
                                  shipment: {
                                    ...editedDetails.shipment,
                                    voyage: e.target.value,
                                  },
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Incoterm</label>
                            <Select
                              value={editedDetails.shipment.incoterm || ''}
                              onValueChange={value =>
                                setEditedDetails({
                                  ...editedDetails,
                                  shipment: {
                                    ...editedDetails.shipment,
                                    incoterm: value,
                                  },
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select Incoterm" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="EXW">EXW - Ex Works</SelectItem>
                                <SelectItem value="FCA">FCA - Free Carrier</SelectItem>
                                <SelectItem value="FAS">FAS - Free Alongside Ship</SelectItem>
                                <SelectItem value="FOB">FOB - Free On Board</SelectItem>
                                <SelectItem value="CFR">CFR - Cost and Freight</SelectItem>
                                <SelectItem value="CIF">CIF - Cost, Insurance and Freight</SelectItem>
                                <SelectItem value="CPT">CPT - Carriage Paid To</SelectItem>
                                <SelectItem value="CIP">CIP - Carriage and Insurance Paid To</SelectItem>
                                <SelectItem value="DAP">DAP - Delivered At Place</SelectItem>
                                <SelectItem value="DPU">DPU - Delivered at Place Unloaded</SelectItem>
                                <SelectItem value="DDP">DDP - Delivered Duty Paid</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Shipment Type</label>
                          <Select
                            value={editedDetails.shipment.shipmentType || ''}
                            onValueChange={value =>
                              setEditedDetails({
                                ...editedDetails,
                                shipment: {
                                  ...editedDetails.shipment,
                                  shipmentType: value,
                                },
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select Shipment Type" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(ShipmentTypeLabels).map(([value, label]) => (
                                <SelectItem key={value} value={value}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="items" className="h-full">
                <div className="flex justify-end mb-2">
                  <Button variant="outline" size="sm" onClick={handleAddItem} className="flex items-center gap-1">
                    <Plus className="h-4 w-4" />
                    Add Item
                  </Button>
                </div>
                <div className="overflow-x-auto" style={{ width: '100%', overflowX: 'auto' }}>
                  <Table style={{ width: '1800px', tableLayout: 'fixed' }}>
                    <TableHeader>
                      <TableRow>
                        <TableHead style={{ width: '70px', paddingLeft: '4px', paddingRight: '4px' }}>Line</TableHead>
                        <TableHead style={{ width: '250px', paddingLeft: '4px', paddingRight: '4px' }}>
                          Description
                        </TableHead>
                        <TableHead style={{ width: '80px', paddingLeft: '4px', paddingRight: '4px' }}>Qty</TableHead>
                        <TableHead style={{ width: '80px', paddingLeft: '4px', paddingRight: '4px' }}>Unit</TableHead>
                        <TableHead style={{ width: '140px', paddingLeft: '4px', paddingRight: '4px' }}>Price</TableHead>
                        <TableHead style={{ width: '100px', paddingLeft: '4px', paddingRight: '4px' }}>
                          Amount
                        </TableHead>
                        <TableHead style={{ width: '180px', paddingLeft: '4px', paddingRight: '4px' }}>
                          HS Code
                        </TableHead>
                        <TableHead style={{ width: '170px', paddingLeft: '4px', paddingRight: '4px' }}>
                          Weight
                        </TableHead>
                        <TableHead style={{ width: '170px', paddingLeft: '4px', paddingRight: '4px' }}>
                          Volume
                        </TableHead>
                        <TableHead style={{ width: '280px', paddingLeft: '4px', paddingRight: '4px' }}>
                          Dimensions
                        </TableHead>
                        <TableHead style={{ width: '120px', paddingLeft: '4px', paddingRight: '4px' }}>
                          Origin
                        </TableHead>
                        <TableHead style={{ width: '40px', paddingLeft: '4px', paddingRight: '4px' }}></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editedDetails.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={12} className="text-center py-4 text-gray-500">
                            No items found
                          </TableCell>
                        </TableRow>
                      ) : (
                        editedDetails.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell style={{ width: '70px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <Input
                                value={item.lineNumber || ''}
                                onChange={e => handleItemChange(index, 'lineNumber', parseInt(e.target.value) || 0)}
                                className="w-full text-center"
                              />
                            </TableCell>
                            <TableCell style={{ width: '250px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <textarea
                                value={item.description || ''}
                                onChange={e => handleItemChange(index, 'description', e.target.value)}
                                className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm resize-y"
                                placeholder="Item description"
                              />
                            </TableCell>
                            <TableCell style={{ width: '80px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <Input
                                value={item.quantity || ''}
                                onChange={e => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-full"
                                type="number"
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell style={{ width: '80px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <UnitTypeSelect
                                value={item.unitOfMeasure || ''}
                                onChange={value => handleItemChange(index, 'unitOfMeasure', value)}
                                category={UnitTypeCategory.PackType}
                                placeholder=""
                              />
                            </TableCell>
                            <TableCell style={{ width: '140px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <div className="flex items-center gap-1">
                                <Input
                                  value={item.unitPrice || ''}
                                  onChange={e => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                  className="flex-1"
                                  type="number"
                                  step="0.01"
                                  placeholder="0"
                                />
                                <AutoWidthSelect
                                  value={item.currency || 'USD'}
                                  onValueChange={value => handleItemChange(index, 'currency', value)}
                                  options={[
                                    { value: 'USD', label: 'USD' },
                                    { value: 'EUR', label: 'EUR' },
                                    { value: 'GBP', label: 'GBP' },
                                    { value: 'CNY', label: 'CNY' },
                                    { value: 'JPY', label: 'JPY' },
                                  ]}
                                  placeholder="USD"
                                />
                              </div>
                            </TableCell>
                            <TableCell style={{ width: '100px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <Input
                                value={item.amount || ''}
                                onChange={e => handleItemChange(index, 'amount', parseFloat(e.target.value) || 0)}
                                className="w-full"
                                type="number"
                                step="0.01"
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell style={{ width: '180px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <div className="flex items-center gap-1">
                                <Input
                                  value={item.hsCode || ''}
                                  onChange={e => handleItemChange(index, 'hsCode', e.target.value)}
                                  className="flex-1"
                                  placeholder="Enter code"
                                />
                                <div className="flex flex-col gap-0">
                                  {item.hsCode && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => showHsCodeInfo(item.hsCode, item.description)}
                                      className="h-6 w-6 p-0 flex-shrink-0"
                                      title="HS Code Information"
                                    >
                                      <HelpCircle className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {onSearchHsCodes && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openHsCodeDialog(index)}
                                      className="h-6 w-6 p-0 flex-shrink-0"
                                      title="Search HS Codes"
                                    >
                                      <Search className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell style={{ width: '170px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <div className="flex items-center gap-1">
                                <Input
                                  value={item.weight || ''}
                                  onChange={e => handleItemChange(index, 'weight', parseFloat(e.target.value) || 0)}
                                  className="flex-1"
                                  type="number"
                                  step="0.01"
                                  placeholder="0"
                                />
                                <UnitTypeSelect
                                  value={item.weightUnit || ''}
                                  onChange={value => handleItemChange(index, 'weightUnit', value)}
                                  category={UnitTypeCategory.Weight}
                                  placeholder=""
                                />
                              </div>
                            </TableCell>
                            <TableCell style={{ width: '170px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <div className="flex items-center gap-1">
                                <Input
                                  value={item.volume || ''}
                                  onChange={e => handleItemChange(index, 'volume', parseFloat(e.target.value) || 0)}
                                  className="flex-1"
                                  type="number"
                                  step="0.001"
                                  placeholder="0"
                                />
                                <UnitTypeSelect
                                  value={item.volumeUnit || ''}
                                  onChange={value => handleItemChange(index, 'volumeUnit', value)}
                                  category={UnitTypeCategory.Volume}
                                  placeholder=""
                                />
                              </div>
                            </TableCell>
                            <TableCell style={{ width: '280px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <div className="flex items-center gap-1 text-xs">
                                <Input
                                  value={item.dimensions?.length || ''}
                                  onChange={e => {
                                    const dimensions = item.dimensions || { length: 0, width: 0, height: 0, unit: 'cm' }
                                    handleItemChange(index, 'dimensions', {
                                      ...dimensions,
                                      length: parseFloat(e.target.value) || 0,
                                    })
                                  }}
                                  className="w-16"
                                  type="number"
                                  step="0.1"
                                  placeholder="L"
                                />
                                <span>×</span>
                                <Input
                                  value={item.dimensions?.width || ''}
                                  onChange={e => {
                                    const dimensions = item.dimensions || { length: 0, width: 0, height: 0, unit: 'cm' }
                                    handleItemChange(index, 'dimensions', {
                                      ...dimensions,
                                      width: parseFloat(e.target.value) || 0,
                                    })
                                  }}
                                  className="w-16"
                                  type="number"
                                  step="0.1"
                                  placeholder="W"
                                />
                                <span>×</span>
                                <Input
                                  value={item.dimensions?.height || ''}
                                  onChange={e => {
                                    const dimensions = item.dimensions || { length: 0, width: 0, height: 0, unit: 'cm' }
                                    handleItemChange(index, 'dimensions', {
                                      ...dimensions,
                                      height: parseFloat(e.target.value) || 0,
                                    })
                                  }}
                                  className="w-16"
                                  type="number"
                                  step="0.1"
                                  placeholder="H"
                                />
                                <UnitTypeSelect
                                  value={item.dimensions?.unit || 'cm'}
                                  onChange={value => {
                                    const dimensions = item.dimensions || { length: 0, width: 0, height: 0, unit: null }
                                    handleItemChange(index, 'dimensions', {
                                      ...dimensions,
                                      unit: value,
                                    })
                                  }}
                                  category={UnitTypeCategory.Length}
                                  placeholder=""
                                />
                              </div>
                            </TableCell>
                            <TableCell style={{ width: '120px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <Input
                                value={item.countryOfOrigin || ''}
                                onChange={e => handleItemChange(index, 'countryOfOrigin', e.target.value)}
                                className="w-full"
                                placeholder="US"
                              />
                            </TableCell>
                            <TableCell style={{ width: '40px', paddingLeft: '4px', paddingRight: '4px' }}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveItem(index)}
                                className="h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="containers" className="h-full">
                <div className="flex justify-end mb-2">
                  <Button variant="outline" size="sm" onClick={handleAddContainer} className="flex items-center gap-1">
                    <Plus className="h-4 w-4" />
                    Add Container
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Container Number</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Seal Number</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editedDetails.containers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                          No containers found
                        </TableCell>
                      </TableRow>
                    ) : (
                      editedDetails.containers.map((container, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Input
                              value={container.number || ''}
                              onChange={e => handleContainerChange(index, 'number', e.target.value)}
                              className="w-full"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={container.size || ''}
                              onValueChange={value => handleContainerChange(index, 'size', value)}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select Size" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="20'">20'</SelectItem>
                                <SelectItem value="40'">40'</SelectItem>
                                <SelectItem value="40'HC">40'HC</SelectItem>
                                <SelectItem value="45'">45'</SelectItem>
                                <SelectItem value="45'HC">45'HC</SelectItem>
                                <SelectItem value="53'">53'</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={container.type || ''}
                              onValueChange={value => handleContainerChange(index, 'type', value)}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Dry">Dry</SelectItem>
                                <SelectItem value="Reefer">Reefer</SelectItem>
                                <SelectItem value="Open Top">Open Top</SelectItem>
                                <SelectItem value="Flat Rack">Flat Rack</SelectItem>
                                <SelectItem value="Tank">Tank</SelectItem>
                                <SelectItem value="Platform">Platform</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={container.sealNumber || ''}
                              onChange={e => handleContainerChange(index, 'sealNumber', e.target.value)}
                              className="w-full"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveContainer(index)}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      ) : null}

      {editedDetails && currentItemIndex !== null && onSearchHsCodes && (
        <HsCodeSearchDialog
          open={hsCodeDialogOpen}
          onOpenChange={setHsCodeDialogOpen}
          initialDescription={editedDetails.items[currentItemIndex]?.description || ''}
          onSelect={handleHsCodeSelect}
          onSearch={onSearchHsCodes}
        />
      )}

      <HsCodeInfoModal
        open={hsCodeInfoOpen}
        onOpenChange={setHsCodeInfoOpen}
        hsCode={hsCodeInfoData.hsCode}
        description={hsCodeInfoData.description}
      />

      {showCloseButton && onClose && (
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </div>
  )
}
